import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import {
  type GraphPathLookupParameters,
  type PathLookupGraphSettings,
  createPathlookupGraphLink,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { FrontendOnlyColumn } from '@ipfabric/utils/table/columns/types/frontendOnlyColumn';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { reloadTableDataInViewport } from 'components/ApiTable/model/commands/reloadTableDataInViewport';
import { Button } from 'components/Button/Button';
import { navigateGraphToLocation } from 'components/Graph/helpers/graphLink';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useApi } from 'utils/request/useApi';

type Row = {
  id: string;
  settings: PathLookupGraphSettings;
  parameters: GraphPathLookupParameters;
  protocol: string;
  src: string;
  dst: string;
  srcPorts?: string;
  dstPorts?: string;
};

const DELETE_CHECK_CONFIRM = 'PATH_VERIFICATION_DELETE_CONFIRM';

const PathVerificationActionsColumn: FC<Row> = (props) => {
  const navigate = useNavigate();
  const api = useApi();
  const ctxProps = useApiTableContextSelectors({
    dispatch: prop('dispatch'),
  });
  const {
    apiTablePage: {
      actionsColumns: {
        pathVerifications: { deleteConfirm: deleteConfirmMsg },
      },
    },
  } = useLocale();
  const deleteDialogId = useHtmlId(DELETE_CHECK_CONFIRM);
  const deleteConfirm = useConfirmDialog({ name: deleteDialogId });

  const handleDeleteClickConfirm = async () => {
    await api.request({
      url: `/graphs/path-lookup/checks/${encodeURIComponent(props.id)}`,
      method: 'DELETE',
    });
    await ctxProps.dispatch(reloadTableDataInViewport);
  };

  const dialogMsgs = {
    ...deleteConfirmMsg,
    header: deleteConfirmMsg.header(
      props.protocol.toUpperCase(),
      `${props.src}${props.srcPorts ?? ''}`,
      `${props.dst}${props.dstPorts ?? ''}`,
    ),
  };

  return (
    <>
      <ConfirmDialog
        id={deleteDialogId}
        msg={dialogMsgs}
        onClickConfirm={handleDeleteClickConfirm}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      <Button
        variant='tertiary'
        icon='fa-eye'
        className='mr-2'
        type='button'
        onClick={() => {
          navigate(
            ...navigateGraphToLocation(
              createPathlookupGraphLink({
                parameters: props.parameters,
                settings: props.settings,
              }),
            ),
          );
        }}
        size='xxs'
      />
      <Button
        variant='tertiary'
        icon='fa-trash-can'
        type='button'
        onClick={() => {
          deleteConfirm.toggleConfirmDialog();
        }}
        size='xxs'
      />
    </>
  );
};

export const pathVerificationsActionsColumn: FrontendOnlyColumn<Row> =
  createFrontendOnlyColumn<Row>({
    key: 'actions',
    title: 'Actions',
    webDependencies: ['id', 'settings', 'parameters'],
    disabledSort: true,
    width: 81,
    fixedWidth: true,
    render: (row: Row) => (
      <PathVerificationActionsColumn
        id={row.id}
        settings={row.settings}
        parameters={row.parameters}
        protocol={row.protocol}
        src={row.src}
        dst={row.dst}
        srcPorts={row.srcPorts}
        dstPorts={row.dstPorts}
      />
    ),
  });
